import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, CheckF, inputCls } from "./AFields";
import { Modal } from "./Participants";

export default function Navigation() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/navigation").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const arr = [...items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setItems(arr);
    try { await api.post("/admin/navigation/reorder", { ids: arr.map((x) => x.id) }); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/navigation/save", editing);
      toast.success("Menu item saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.label_en}"?`)) return;
    try {
      await api.delete(`/admin/navigation/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleVisible = async (item) => {
    try {
      await api.post("/admin/navigation/save", { ...item, visible: !item.visible });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-navigation">
      <SEO title="Navigation" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Navigation</h1>
          <p className="mt-1 text-sm text-slate-500">Add, rename, reorder, hide or delete menu items. System items are protected from deletion.</p>
        </div>
        <button onClick={() => setEditing({ label_en: "", label_hi: "", url: "", visible: true, external: false })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="nav-new-btn"><Plus size={16} /> Add Menu Item</button>
      </div>

      <div className="mt-6 max-w-3xl space-y-2">
        {items.map((item, i) => (
          <div key={item.id} className={`flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 ${!item.visible ? "opacity-55" : ""}`} data-testid={`nav-item-${item.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-navy disabled:opacity-30" aria-label="Move up" data-testid={`nav-up-${item.id}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-slate-400 hover:text-navy disabled:opacity-30" aria-label="Move down" data-testid={`nav-down-${item.id}`}><ArrowDown size={14} /></button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-navy">{item.label_en}{item.label_hi ? <span className="ml-2 font-medium text-slate-500">{item.label_hi}</span> : null}</p>
              <p className="text-xs text-slate-400">{item.url} {item.external && "↗ new tab"} {item.is_system && <span className="ml-2 rounded-full bg-navy-50 px-2 py-0.5 text-[10px] font-bold text-navy">SYSTEM</span>}</p>
            </div>
            <button onClick={() => toggleVisible(item)} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" aria-label="Show or hide" data-testid={`nav-toggle-${item.id}`}>
              {item.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button onClick={() => setEditing(item)} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`nav-edit-${item.id}`}><Pencil size={14} /></button>
            {!item.is_system && (
              <button onClick={() => remove(item)} className="rounded-md border border-gray-200 p-2 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`nav-delete-${item.id}`}><Trash2 size={14} /></button>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? `Edit — ${editing.label_en}` : "New Menu Item"} testid="nav-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Label (English)" value={editing.label_en} onChange={(v) => setEditing({ ...editing, label_en: v })} required />
            <TextF label="Label (Hindi)" value={editing.label_hi} onChange={(v) => setEditing({ ...editing, label_hi: v })} />
            <TextF label="URL" value={editing.url} onChange={(v) => setEditing({ ...editing, url: v })} required placeholder="/about or https://…" />
          </div>
          <div className="mt-5 flex gap-6">
            <CheckF label="Visible" value={editing.visible} onChange={(v) => setEditing({ ...editing, visible: v })} />
            <CheckF label="Open external link in new tab" value={editing.external} onChange={(v) => setEditing({ ...editing, external: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="nav-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
